import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Loader from '@/components/Loader';
import { useGetWorkOrderById } from '@/services/workOrderService';
import {
  confirmProcessBillingVendor,
  getProcessBillingVendor,
  sendProcessBillingVendorAffidavit,
} from '@/services/processBillingVendorService';
import { useUserStore } from '@/stores/user';
import { isAdminOrSupervisor as isAdminOrSupervisorUser, isBillingTeamMember } from '@/services/userService';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit, FileText, Send } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';
import { OneLineText } from '@/components/OneLineText';
import { DocumentListDialog } from '@/components/DocumentListDialog';
import { useMemo, useState } from 'react';
import type { DocumentListType, MediaBaseResponse } from '@/types/types';
import type { ProcessBillingVendorDocumentResponse } from '@/types/processBillingVendor.types';
import { SendAffidavitConfirmDialog } from '@/pages/caseManagement/processBilling/SendAffidavitConfirmDialog';
import { getVendorPrefillForBilling } from '@/pages/caseManagement/processBilling/processBillingVendorUtils';
import { toast } from '@/components/ui/use-toast';

const PROOF_OF_SERVICE_HELPER = 'Proof of Service is required. Upload one or more PDF files (max 20 MB each).';

export const ProcessBillingVendorView = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const queryClient = useQueryClient();

  const woQuery = useGetWorkOrderById(workOrderId, { refetchOnMount: 'always' });
  const billingQuery = useQuery({
    queryKey: ['processBillingVendor', workOrderId],
    queryFn: () => getProcessBillingVendor(workOrderId!),
    enabled: Boolean(workOrderId),
    retry: false,
  });

  const roleId = user?.roleId as string | undefined;
  const isAdminOrSupervisor = isAdminOrSupervisorUser(user);
  const billingData = billingQuery.data;
  const entry = billingData?.entry ?? null;
  const hasBilling = Boolean(billingData?.processBillingBatchId && entry);
  const isConfirmed = entry?.status === 'CONFIRMED';
  const isBillingRole = roleId === 'BILLING' || isBillingTeamMember(user);

  const canEdit = hasBilling && isAdminOrSupervisor;
  const canConfirm = hasBilling && !isConfirmed && (isAdminOrSupervisor || isBillingRole);

  const allDocuments = useMemo<DocumentListType[]>(() => {
    if (!entry) return [];
    return entry.proofOfService.map((doc) => ({
      name: doc.name,
      media: { mediaId: doc.mediaId } as MediaBaseResponse,
      affidavitSendDocumentId: doc.processBillingVendorDocumentId,
    }));
  }, [entry]);

  const [openDocumentDialog, setOpenDocumentDialog] = useState(false);
  const [activeDocumentMediaId, setActiveDocumentMediaId] = useState<string | undefined>();
  const activeDocumentIndex = useMemo(() => {
    if (!activeDocumentMediaId) return 0;
    const idx = allDocuments.findIndex((d) => d.media?.mediaId === activeDocumentMediaId);
    return idx >= 0 ? idx : 0;
  }, [allDocuments, activeDocumentMediaId]);

  const [pendingSendVendorDocumentId, setPendingSendVendorDocumentId] = useState<string | null>(null);

  const openDocument = (doc: ProcessBillingVendorDocumentResponse) => {
    setActiveDocumentMediaId(doc.mediaId);
    setOpenDocumentDialog(true);
  };

  const confirmMutation = useMutation({
    mutationFn: () => confirmProcessBillingVendor(workOrderId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['processBillingVendor', workOrderId] });
      toast({ description: 'Vendor billing confirmed.' });
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
          : undefined;
      toast({ description: msg ?? 'Failed to confirm vendor billing.', variant: 'destructive' });
    },
  });

  const woNumber = woQuery.data?.number ?? '';
  const goEdit = () => navigate(`/process/workorder/${workOrderId}/process-billing-vendor/edit`);
  const goCreate = () => navigate(`/process/workorder/${workOrderId}/process-billing-vendor/create`);

  // If the WO has the side panel populated but vendor entry doesn't exist, surface the autofilled vendor.
  const vendorPrefill = getVendorPrefillForBilling(woQuery.data);
  const vendorName = entry?.vendorName ?? vendorPrefill?.vendorName ?? '';
  const vendorContactName = entry?.vendorContactName ?? vendorPrefill?.vendorContactName ?? '';

  return (
    <Loader isLoading={woQuery.isFetching || billingQuery.isFetching}>
      <div className="w-full h-full min-w-[170px] flex flex-col gap-6">
        <div className="flex flex-row items-center justify-between">
          <h1 className="text-2xl font-semibold text-primary tracking-tight">
            {`W/O: ${woNumber} - Vendor Billing Entry`}
          </h1>
          {canEdit ? (
            <button
              type="button"
              aria-label="Edit vendor billing"
              className="bg-white border border-bordercolor rounded-md p-1.5 hover:bg-[#EDF5FF]"
              onClick={goEdit}
            >
              <Edit className="h-5 w-5 text-placeholder" />
            </button>
          ) : null}
        </div>

        {billingQuery.isError ? (
          <div className="text-sm text-destructive">Could not load vendor billing for this work order.</div>
        ) : null}

        {!billingQuery.isFetching && !hasBilling && !billingQuery.isError ? (
          <div className="flex flex-col items-start gap-3 rounded-lg bg-[#F5F5F5] p-6 text-sm">
            <span className="text-placeholder">No vendor billing entry on file for this work order yet.</span>
            <Button type="button" onClick={goCreate}>
              Add Vendor Billing
            </Button>
          </div>
        ) : null}

        {hasBilling && entry ? (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <div className="flex flex-col gap-1">
                <Label className="text-sm font-medium">Vendor Name</Label>
                <Input
                  value={vendorName}
                  readOnly
                  disabled
                  className="bg-[#F5F5F5] border-bordercolor text-placeholder disabled:opacity-100 disabled:cursor-default"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-sm font-medium">Vendor Contact Name</Label>
                <Input
                  value={vendorContactName ?? '—'}
                  readOnly
                  disabled
                  className="bg-[#F5F5F5] border-bordercolor text-placeholder disabled:opacity-100 disabled:cursor-default"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-sm font-medium">Vendor Server Name</Label>
                <Input
                  value={entry.vendorServerName ?? '—'}
                  readOnly
                  disabled
                  className="bg-white border-bordercolor text-[#0A0A0A] disabled:opacity-100 disabled:cursor-default"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-sm font-medium">Server ID</Label>
                <Input
                  value={entry.serverId ?? '—'}
                  readOnly
                  disabled
                  className="bg-white border-bordercolor text-[#0A0A0A] disabled:opacity-100 disabled:cursor-default"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-sm font-medium">Vendor Invoice Number</Label>
                <Input
                  value={entry.vendorInvoiceNumber}
                  readOnly
                  disabled
                  className="bg-white border-bordercolor text-[#0A0A0A] disabled:opacity-100 disabled:cursor-default"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-sm font-medium">Amount Due</Label>
                <Input
                  value={formatCurrency(entry.amountDue)}
                  readOnly
                  disabled
                  className="bg-white border-bordercolor text-[#0A0A0A] disabled:opacity-100 disabled:cursor-default"
                />
              </div>
            </div>

            <div className="mt-8 rounded-lg bg-[#F5F5F5] p-5">
              <div className="flex flex-row justify-between items-start mb-4">
                <div>
                  <div className="text-base font-semibold text-primary">Vendor Proof of Service</div>
                  <p className="text-sm text-placeholder italic">{PROOF_OF_SERVICE_HELPER}</p>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                {entry.proofOfService.map((doc) => (
                  <div
                    key={doc.processBillingVendorDocumentId}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end"
                  >
                    <div className="flex flex-col gap-1">
                      <Label className="text-sm font-medium">Document Name</Label>
                      <Input
                        value={doc.name}
                        readOnly
                        disabled
                        className="bg-white border-bordercolor text-[#0A0A0A] disabled:opacity-100 disabled:cursor-default"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium invisible select-none">spacer</span>
                      <div
                        className="flex flex-row items-center gap-2 rounded-md bg-white border border-dashed border-[#92A1B3] px-4 h-12 cursor-pointer"
                        onClick={() => openDocument(doc)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openDocument(doc);
                          }
                        }}
                      >
                        <FileText className="h-5 w-5 text-primary shrink-0" />
                        <OneLineText text={doc.name} className="flex-1 text-sm text-primary" />
                        <button
                          type="button"
                          aria-label={`Send ${doc.name}`}
                          className="text-primary hover:text-[#12375C]"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingSendVendorDocumentId(doc.processBillingVendorDocumentId);
                          }}
                        >
                          <Send className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        <div className="flex flex-row gap-3 justify-end mt-6">
          <Button
            type="button"
            variant="outline"
            className="w-[125px] h-[48px] border-primary text-primary hover:bg-[#EDF5FF]"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          {canConfirm ? (
            <Button
              type="button"
              className="h-[48px] px-6 hover:bg-[#12375C]"
              disabled={confirmMutation.isPending}
              onClick={() => confirmMutation.mutate()}
            >
              {confirmMutation.isPending ? 'Confirming…' : 'Save and Confirm'}
            </Button>
          ) : null}
        </div>
      </div>
      {openDocumentDialog && allDocuments.length > 0 ? (
        <DocumentListDialog
          documents={allDocuments}
          activeIndex={activeDocumentIndex}
          onCloseClick={() => setOpenDocumentDialog(false)}
          onRequestSendAffidavit={(id) => setPendingSendVendorDocumentId(id)}
        />
      ) : null}
      {pendingSendVendorDocumentId && workOrderId ? (
        <SendAffidavitConfirmDialog
          workOrderId={workOrderId}
          documentIds={[pendingSendVendorDocumentId]}
          clientId={woQuery.data?.service?.client?.clientId ?? null}
          sendFn={sendProcessBillingVendorAffidavit}
          onClose={() => setPendingSendVendorDocumentId(null)}
        />
      ) : null}
    </Loader>
  );
};
